/**
 * Auto-save (signed-in consumers only): quietly persists EVERYTHING — the plan, where they're up
 * to, saved strategies/scenarios, SOA case studies and service plans — a few seconds after each
 * change, plus a 30-second sweep for work stored outside React state (e.g. SAH service plans).
 * Partner-portal demo journeys are excluded on purpose. Extracted out of App.tsx.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { autoSaveClientProfile, captureSessionMirror, isProfileChangedError, saveClientProfile } from '../services/profileApi.ts';
import type { SessionUser } from '../services/authClient.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { SavedStrategy, SavedCaseStudy } from '../pages/Dashboard/dashboardConfig.tsx';
import type { CoupleValidationState } from '../features/agedCare/AgedCareAnalyser.tsx';

export interface UseAutoSaveParams {
  accountUser: SessionUser | null;
  activeProfessionalCaseId: string | null;
  activeRealProfileId: string | null;
  activePage: string;
  factFind: DraftFactFind;
  clientProfileStep: number;
  savedStrategies: SavedStrategy[];
  savedCaseStudies: SavedCaseStudy[];
  coupleValidation: CoupleValidationState;
}

export interface UseAutoSaveResult {
  /**
   * Ensures a save reflecting the CURRENT state has been attempted, and waits for it to settle
   * (success or failure) before resolving — call this before signing out. Without it, sign-out can
   * race an in-flight or about-to-fire autosave: `authSignOut()` invalidates the session token, so
   * a save still in flight (or one queued behind it) fails auth and is silently dropped by
   * `performAutoSave`'s `.catch()`, losing whatever was edited in the last few seconds even though
   * an EARLIER, less complete autosave already succeeded and looks fine. Bounded to ~8s so a dead
   * network can't hang the sign-out button forever — losing the very last autosave in that case is
   * an acceptable trade-off against blocking sign-out indefinitely.
   */
  flushPendingSave: () => Promise<void>;
  /**
   * Why the latest save was refused, in words for the person at the screen — null while saves
   * are landing. A rejected save used to be a console line nobody saw (owner, 3 Oct 2026: a
   * selected plan vanished from the dashboard); now it is said on screen, with a retry.
   */
  saveFailure: string | null;
  retrySave: () => void;
  /**
   * The file was saved elsewhere (another window, another person) since it was opened here. Saving stops
   * so this copy cannot overwrite that work; the page offers a reload instead of a retry.
   */
  savedElsewhere: boolean;
}

/** The one-line reason a save was refused, from whatever the request threw. */
function describeSaveFailure(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } }; message?: string } | null;
  const status = e?.response?.status;
  const code = e?.response?.data?.error?.code;
  if (status === 422 || code === 'INVALID_FACT_FIND') return 'The server did not accept the shape of the file (it may be running an older version). Nothing changed since your last successful save has been kept.';
  // A firm member's account must have two-step sign-in before it can save; told as what it is, not
  // as an expired session (owner, 16 Oct 2026, testing a client invite from an adviser's own login).
  // Only a genuine change to the name or contact details is refused now (the server ignores an
  // unchanged identity); say which half did not land rather than blaming the session.
  if (code === 'PROFILE_CHANGED') return 'This file was saved in another window or by someone else since you opened it. Reload to see the latest; nothing was overwritten.';
  if (code === 'IDENTITY_READ_ONLY') return 'Your changes were saved, except your name and contact details, which your adviser keeps. Ask them if those need updating.';
  if (code === 'MFA_SETUP_REQUIRED') return 'Saving needs two-step sign-in on this account. Set up your authenticator under Account, then try again.';
  if (status === 401 || status === 403) return 'Your sign-in has expired. Sign in again to keep saving.';
  if (status === 402) return 'Your plan does not allow saving at the moment.';
  if (!status) return 'The server could not be reached.';
  return e?.response?.data?.error?.message || e?.message || 'Something went wrong saving your work.';
}

export function useAutoSave({
  accountUser,
  activeProfessionalCaseId,
  activeRealProfileId,
  activePage,
  factFind,
  clientProfileStep,
  savedStrategies,
  savedCaseStudies,
  coupleValidation,
}: UseAutoSaveParams): UseAutoSaveResult {
  const autosaveEnabled =
    Boolean(accountUser) &&
    (!activeProfessionalCaseId || Boolean(activeRealProfileId)) &&
    activePage !== 'LandingPage' && activePage !== 'PartnerPortal';

  // Two overlapping saves race on the network: an earlier request (started first, e.g. while a
  // field was still mid-edit) can resolve AFTER a later, more complete one, silently overwriting
  // it with stale data — the PUT/POST here has no server-side ordering guard (no ETag/version
  // check), so the client must never have more than one save in flight. `savingRef` enforces that;
  // a save requested while one is already in flight just marks `pendingRef` and re-fires (with
  // whatever is CURRENT at that time, via `latestSaveRef`) once the in-flight one finishes — so the
  // most recent state always eventually lands, in the order it was produced. This also prevents a
  // second, duplicate profile being created: on a brand-new profile, autoSaveClientProfile POSTs
  // and only then learns the new profileId — a second save firing before that POST resolves would
  // otherwise see no stored id and POST its own separate profile record.
  const savingRef = useRef(false);
  const pendingRef = useRef(false);
  const latestSaveRef = useRef<() => void>(() => {});
  const [saveFailure, setSaveFailure] = useState<string | null>(null);
  // Saved elsewhere since this copy was opened: stop saving until the page is reloaded (23 Oct 2026).
  const [savedElsewhere, setSavedElsewhere] = useState(false);
  const savedElsewhereRef = useRef(false);
  /*
   * WHAT WAS LAST SAVED, so an unchanged file is not sent again (owner, 23 Oct 2026). The 30-second sweep
   * re-sent the whole file every time whether or not anything had changed; a forgotten window doing that
   * is how older work overwrote newer. A save now goes only when the file differs from the last one that
   * landed; the version check (profileApi) refuses anything stale that still gets through.
   */
  const lastSavedRef = useRef<string | null>(null);
  // Another file opened in this tab starts afresh: the refusal was about the file it came from.
  useEffect(() => {
    savedElsewhereRef.current = false;
    setSavedElsewhere(false);
    setSaveFailure(null);
  }, [activeRealProfileId]);

  const performAutoSave = useCallback(() => {
    if (savedElsewhereRef.current) return;
    const payload = {
      factFind,
      appState: { lastPage: activePage, clientProfileStep },
      appData: {
        savedStrategies,
        savedCaseStudies,
        coupleValidation,
        sessionMirror: captureSessionMirror(),
      },
    };
    if (savingRef.current) {
      pendingRef.current = true;
      return;
    }
    let snapshot: string | null = null;
    try { snapshot = JSON.stringify([activeRealProfileId, payload]); } catch { snapshot = null; }
    if (snapshot !== null && snapshot === lastSavedRef.current) return;
    savingRef.current = true;
    // Adviser working on a REAL client file → save to that record; otherwise the user's own plan.
    const save = activeRealProfileId
      ? saveClientProfile({ ...payload, profileId: activeRealProfileId })
      : autoSaveClientProfile(payload);
    save
      .then(() => { lastSavedRef.current = snapshot; setSaveFailure(null); })
      .catch((err: unknown) => {
        if (process.env.NODE_ENV === 'development') console.warn('[autosave]', err instanceof Error ? err.message : err);
        if (isProfileChangedError(err)) { savedElsewhereRef.current = true; setSavedElsewhere(true); }
        setSaveFailure(describeSaveFailure(err));
      })
      .finally(() => {
        savingRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          latestSaveRef.current();
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factFind, activePage, clientProfileStep, savedStrategies, savedCaseStudies, coupleValidation, activeRealProfileId]);

  useEffect(() => {
    latestSaveRef.current = performAutoSave;
  }, [performAutoSave]);

  useEffect(() => {
    if (!autosaveEnabled) return undefined;
    const timer = setTimeout(performAutoSave, 4000);
    return () => clearTimeout(timer);
  }, [autosaveEnabled, performAutoSave]);

  useEffect(() => {
    if (!autosaveEnabled) return undefined;
    const interval = setInterval(performAutoSave, 30000);
    return () => clearInterval(interval);
  }, [autosaveEnabled, performAutoSave]);

  /*
   * A SELECTED PLAN IS SAVED AT ONCE (owner, 3 Oct 2026). Selecting a strategy is the act the
   * dashboard and the advice document hang off; it used to wait out the four-second debounce like
   * a keystroke, and a reload inside that window (a Statement of Advice opening in a new tab, a
   * dev-server restart) lost it with nothing said. The count moving is the signal.
   */
  const savedCountRef = useRef(savedStrategies.length + savedCaseStudies.length);
  useEffect(() => {
    const count = savedStrategies.length + savedCaseStudies.length;
    if (count === savedCountRef.current) return;
    savedCountRef.current = count;
    if (autosaveEnabled) performAutoSave();
  }, [autosaveEnabled, performAutoSave, savedStrategies.length, savedCaseStudies.length]);

  const retrySave = useCallback(() => { latestSaveRef.current(); }, []);

  const flushPendingSave = useCallback((): Promise<void> => {
    if (!autosaveEnabled) return Promise.resolve();
    // Make sure a save reflecting the latest state is at least queued (if one's already in
    // flight, this just sets pendingRef, which is exactly what we want to wait out below).
    latestSaveRef.current();
    const deadline = Date.now() + 8000;
    return new Promise((resolve) => {
      const poll = () => {
        if ((!savingRef.current && !pendingRef.current) || Date.now() > deadline) {
          resolve();
          return;
        }
        setTimeout(poll, 50);
      };
      poll();
    });
  }, [autosaveEnabled]);

  return { flushPendingSave, saveFailure, retrySave, savedElsewhere };
}
